import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, EnvelopeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { validateEmail, sanitizeEmail } from '../utils/emailValidation';

// API base URL - use relative URL for production to work with Vercel
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5001/api'
  : '/api';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const footerLinks = {
    wellness: [
      { name: 'Stress Relief', href: '/category/stress-relief' },
      { name: 'Mindfulness', href: '/category/mindfulness' },
      { name: 'Sleep Better', href: '/category/sleep' },
      { name: 'Self Care', href: '/category/self-care' },
    ],
    resources: [
      { name: 'About Us', href: '/about' },
      { name: 'Contact', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
    ],
  };

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Email validation
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setStatus('error');
      setMessage(validation.message);
      return;
    }

    const sanitizedEmail = sanitizeEmail(email);
    setStatus('loading');
    
    try {
      console.log('Submitting to:', `${API_BASE_URL}/newsletter/subscribe`);
      
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: sanitizedEmail }),
      });

      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Successfully subscribed!');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-9 h-9">
                <img src="/favicon.svg" alt="Destress Daily Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-bold">Destress Daily</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your daily source for practical health tips, wellness hacks, and evidence-based advice to improve your life.
            </p>
          </div>

          {/* Daily Tips */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Daily Tips</h3>
            <ul className="space-y-2">
              {footerLinks.wellness.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={handleLinkClick}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Signup */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Stay Updated</h3>
            <p className="text-gray-400 text-sm mb-4">
              Get daily stress relief tips delivered to your inbox.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 newsletter-input"
                  disabled={status === 'loading'}
                />
              </div>
              
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-white text-gray-900 py-3 px-6 rounded-lg font-semibold hover:bg-gray-100 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none border-2 border-transparent hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                {status === 'loading' ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner h-4 w-4 mr-2 border-gray-300 border-t-gray-900"></div>
                    Subscribing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    Subscribe
                  </div>
                )}
              </button>
              
              {status === 'success' && (
                <div className="flex items-center text-green-400 text-sm">
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {message}
                </div>
              )}
              
              {status === 'error' && (
                <div className="text-red-400 text-sm">
                  {message}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © {currentYear} Destress Daily. All rights reserved.
            </p>
            <div className="flex items-center space-x-2 text-gray-400 text-sm mt-4 md:mt-0">
              <span>Made with</span>
              <HeartIcon className="h-4 w-4 text-red-500" />
              <span>for your health</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 